import { useState, useRef, useMemo } from 'react';
import debounce from 'lodash/debounce';
import type FetchError from '@atlassian/jira-fetch/src/utils/errors.tsx';
import type { ContextAndValuesOfIssueBeingCreated } from '@atlassian/jira-issue-create-common-types/src/common/types/index.tsx';
import { autocompleteInsightFieldExperience } from '@atlassian/jira-servicedesk-insight-experiences/src/index.tsx';
import { extractFieldValueMap } from '../common/utils/extract-field-value-map/index.tsx';
import {
	type SearchedObject,
	Actions,
	type Action,
	type PaginatedObjectAutocompleteReturnType,
	type SearchReturnType,
	type InputActionMeta,
	type OptionsType,
} from './types.tsx';

const filterOutAlreadySelectedOptionFromAvailableOptions = <T extends SearchedObject>(
	availableOptions: T[],
	optionsToRemove: T[],
) =>
	availableOptions.filter(
		(optionFromRequest) =>
			!optionsToRemove.find((selectedOption) => selectedOption.id === optionFromRequest.id),
	);

const addOrReplaceNewResults =
	<T extends SearchedObject>(action: string, optionsToSave: T[]) =>
	(oldOptions: T[]) => {
		switch (action) {
			case Actions.APPEND:
				return [...oldOptions, ...optionsToSave];
			case Actions.REPLACE:
			default:
				return optionsToSave;
		}
	};

const calculateNumberOfResults = <T extends SearchedObject>(
	totalNumberOfResults: number | undefined,
	savedResultFromTheSearch: T[],
	displayedOptions: OptionsType<T>[],
) => {
	if (totalNumberOfResults)
		return totalNumberOfResults - (savedResultFromTheSearch.length - displayedOptions.length);
	return totalNumberOfResults;
};

export const usePaginatedAutocomplete = <T extends SearchedObject>(
	selectedResults: T[],
	doFetch: (arg1: string) => Promise<SearchReturnType<T>>,
	transform: (arg1: T) => OptionsType<T>,
	contextAndValuesOfIssueBeingCreated?: ContextAndValuesOfIssueBeingCreated,
): PaginatedObjectAutocompleteReturnType<T> => {
	const [isLoading, setIsLoading] = useState(false);

	const [savedResultFromTheSearch, setSavedResultFromTheSearch] = useState<T[]>([]);
	const [hasUserEnteredTextForLastSearch, setHasUserEnteredTextForLastSearch] = useState<
		boolean | undefined
	>(undefined);
	const [totalNumberOfResults, setTotalNumberOfResults] = useState<number | undefined>();
	const [error, setError] = useState<undefined | FetchError>();

	const fetchNextPageRef = useRef<() => Promise<SearchReturnType<T>>>();
	const isMenuOpen = useRef(false);
	const oldContextRef = useRef<{ [key: string]: string }>();

	const fetchSearchResults = async (
		fetchFunction: () => Promise<SearchReturnType<T>>,
		action: Action,
	) => {
		setIsLoading(true);
		try {
			const { results, fetchNextPage: nextPageFunction, total } = await fetchFunction();
			setTotalNumberOfResults(total);

			const newOptions = filterOutAlreadySelectedOptionFromAvailableOptions<T>(
				results,
				selectedResults,
			);

			if (newOptions.length === 0 && nextPageFunction) {
				await fetchSearchResults(nextPageFunction, Actions.APPEND);
			} else {
				fetchNextPageRef.current = nextPageFunction;
			}

			setSavedResultFromTheSearch(addOrReplaceNewResults<T>(action, results));

			autocompleteInsightFieldExperience.success();
			setIsLoading(false);
			// eslint-disable-next-line @typescript-eslint/no-explicit-any
		} catch (err: any) {
			fetchNextPageRef.current = undefined;
			autocompleteInsightFieldExperience.failure();

			if (err && err.name === 'AbortError') {
				return;
			}
			setError(err);
			setIsLoading(false);
		}
	};

	const onInputChange = (searchValue: string, type: InputActionMeta) => {
		// Fetch search results not triggered on selection of object
		if (type.action === 'input-change') {
			autocompleteInsightFieldExperience.start();
			setHasUserEnteredTextForLastSearch(searchValue !== '');
			fetchSearchResults(() => doFetch(searchValue), Actions.REPLACE);
		}
	};

	const debouncedOnInputChange = debounce(onInputChange, 400);

	const onMenuScrollToBottom = async () => {
		if (fetchNextPageRef.current) {
			autocompleteInsightFieldExperience.start();
			await fetchSearchResults(fetchNextPageRef.current, Actions.APPEND);
		}
	};

	const hasContextChanged = (currentContext: { [key: string]: string }) => {
		const fieldId = contextAndValuesOfIssueBeingCreated?.fieldId;
		const oldContext = oldContextRef.current;

		if (!oldContext || !currentContext) return false;

		const currentContextKeys = Object.keys(currentContext);
		const oldContextKeys = Object.keys(oldContext);

		if (currentContextKeys.length !== oldContextKeys.length) return true;

		for (let i = 0; i < currentContextKeys.length; i += 1) {
			const key = currentContextKeys[i];

			const value1 = currentContext[key];
			const value2 = oldContext[key];
			const isCheckingValueOfCurrentField = fieldId === key;

			if (!isCheckingValueOfCurrentField && value1 !== value2) {
				return true;
			}
		}
		return false;
	};

	const onMenuOpen = async () => {
		if (!isMenuOpen.current) {
			isMenuOpen.current = true;
			autocompleteInsightFieldExperience.start();
			const currentContext = extractFieldValueMap(contextAndValuesOfIssueBeingCreated);
			if (hasUserEnteredTextForLastSearch !== false || hasContextChanged(currentContext)) {
				await fetchSearchResults(() => doFetch(''), Actions.REPLACE);
				setHasUserEnteredTextForLastSearch(false);
				oldContextRef.current = currentContext;
			} else {
				autocompleteInsightFieldExperience.success();
			}
		}
	};

	const onMenuClose = () => {
		isMenuOpen.current = false;
	};

	const displayedOptions = useMemo(
		() =>
			filterOutAlreadySelectedOptionFromAvailableOptions(
				savedResultFromTheSearch,
				selectedResults,
			).map(transform),
		[savedResultFromTheSearch, selectedResults, transform],
	);

	return {
		error,
		selectProps: {
			isLoading,
			options: displayedOptions,
			onInputChange: debouncedOnInputChange,
			onMenuScrollToBottom,
			onMenuOpen,
			onMenuClose,
		},
		totalNumberOfResults: calculateNumberOfResults<T>(
			totalNumberOfResults,
			savedResultFromTheSearch,
			displayedOptions,
		),
	};
};
